/**
 * Admin card to upload open RMAs CSV for a selected Global-e portal (activated merchant).
 */
import { observer } from "mobx-react-lite";
import React from "react";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import { Button } from "../../../components/inputs/button/button";
import Card from "../../../components/layouts/card/card";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../components/fonts/font-props";
import ShopsSelector from "../../../components/shops-selector/shops-selector";
import { FormFieldLine } from "../../../components/form-field-line/form-field-line";
import FileUpload from "../../../components/file-upload/file-upload";
import uploadOpenRmasStore from "./stores/upload-open-rmas-store";
import { isSet } from "../../../models/model-types-methods";
import "./migrate-global-e-merchants.scss";
import { IShop } from "../../../models/User";

/** Includes empty string for browsers that omit `file.type` for some `.csv` picks. */
const CSV_MIME_TYPES = ["text/csv", "application/vnd.ms-excel", "application/csv", "text/plain", ""];

const UploadOpenRmasCard: React.FC = observer(() => {
    const store = uploadOpenRmasStore;

    return (
        <Card
            isExpanded={true}
            toolbar={{
                title: (
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        Upload Open RMAs for an Activated Global-e Merchant
                    </FontBody1>
                ),
            }}
        >
            <ListLayout>
                <FormFieldLine label="Select a Global-e portal to upload Open RMAs CSV file into">
                    <ShopsSelector
                        value={store.portal ? [store.portal] : ([] as IShop[])}
                        onChange={(shops: IShop[]) => store.setPortal(shops[0])}
                        disabled={store.isProcessing}
                        includeSecondaryPortals
                    />
                </FormFieldLine>

                <FormFieldLine label="CSV file">
                    <FileUpload
                        uploadFile={(file) => {
                            store.setCsvFile(file);
                        }}
                        buttonText="Select CSV file"
                        acceptedFileFormats=".csv,text/csv"
                        fileTypes={CSV_MIME_TYPES}
                        disabled={store.isProcessing}
                        isLoading={store.isProcessing}
                    />
                    {store.file ? (
                        <FontBody1 color={FONT_COLOR.PRIMARY2}>{store.file.name}</FontBody1>
                    ) : null}
                </FormFieldLine>

                <Button
                    onClick={() => store.upload()}
                    text="Upload"
                    hasLoader={true}
                    isLoading={store.isProcessing}
                    disabled={
                        store.isProcessing ||
                        !isSet(store.portal) ||
                        !store.file
                    }
                />

                {store.infoMessage ? (
                    <div className="results-message">
                        <FontBody1 color={FONT_COLOR.PRIMARY2}>{store.infoMessage}</FontBody1>
                    </div>
                ) : null}
            </ListLayout>
        </Card>
    );
});

export default UploadOpenRmasCard;
